import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, ButtonSelect, Icon, Menu } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import {
  getCertDetailsData,
  selectAllBulkAction,
  setAlteastOneIsSelected,
  setBulkActionAlert,
  setCertTriggerFromSearchOrFilter,
  setCheckForBulkAction,
  setGroupByOptions,
  setIndeterminateForBulkAction,
  setPersonalizationStatus,
} from '../../../redux/certdetails/action';
import {
  GROUP_BY_APP_VALUE,
  GROUP_BY_USER_VALUE,
  ITEMS_API_OFFSET,
  SCREEN_SIZE_TABLET_XSMALL,
  SCREEN_WIDTH_QUERIES,
  VIEW_OPTIONS,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';

import invertedMessages from './messages';

import './GroupBy.css';

function GroupByButton({
  isBadgeGroupBy,
  groupByOptions,
  isButtonKindIcon,
  label,
  resetMergeList,
}) {
  const dispatch = useDispatch();

  const {
    bulkAction: {
      isChecked: isBulkChecked,
      isIndeterminate: isBulkIndeterminate,
    },
    core: { columnNames, searchText, isLoading },
    global: { mediaQueryValue, certKey },
    tabs: { tabData },
    viewControls: { groupByOptions: currentGroupByOptions },
    personalization: { personalizedData, viewOption },
  } = useSelector((state) => state?.certifications?.details);

  const l = useInvertedLocalize(invertedMessages);

  const personalizedViewData = personalizedData?.result?.info?.defaultView;
  const activeTabCount = tabData?.find((tab) => tab.isActive)?.count;
  const tabletXSmallQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_XSMALL
  );

  // to set the personalised values
  useEffect(() => {
    if (
      personalizedViewData &&
      personalizedViewData[0] &&
      personalizedViewData[0]?.groupByFilter &&
      viewOption?.view === VIEW_OPTIONS.MY_PERSONALIZED_VIEW
    ) {
      const personalisedGroupByApplicationValue =
        personalizedViewData[0]?.groupByFilter;
      const updatedGroupByOptions = currentGroupByOptions.map((option) => ({
        ...option,
        isChecked: option.value === personalisedGroupByApplicationValue,
      }));

      dispatch(setGroupByOptions(updatedGroupByOptions));
    } else {
      const updatedGroupByOptions = currentGroupByOptions.map((option) => ({
        ...option,
        isChecked: option.value === GROUP_BY_USER_VALUE,
      }));

      dispatch(setGroupByOptions(updatedGroupByOptions));
    }
  }, [personalizedData, viewOption]);

  const isTabletXSmallWidth = tabletXSmallQuery.value <= mediaQueryValue;

  const currentSelectedGroupByValue = currentGroupByOptions?.find(
    (option) => option.isChecked
  )?.value;

  const handleGroupByChange = (selectedOption) => {
    const updatedGroupByOptions = groupByOptions.map((option) => ({
      ...option,
      isChecked: option.value === selectedOption.value,
    }));

    // condition for handling personalisation with group by filter
    if (personalizedViewData && personalizedViewData[0]) {
      const filterValue = personalizedViewData[0]?.groupByFilter;
      const enablePersonalisationUpdate =
        (!filterValue && selectedOption?.value === GROUP_BY_APP_VALUE) ||
        (filterValue && filterValue !== selectedOption?.value);

      if (
        enablePersonalisationUpdate ||
        viewOption?.view === VIEW_OPTIONS.DEFAULT_VIEW
      ) {
        dispatch(setPersonalizationStatus('UPDATE_PERSONALIZATION'));
      } else {
        dispatch(setPersonalizationStatus('EXIST_PERSONALIZATION'));
      }
    }

    if (selectedOption?.value !== currentSelectedGroupByValue) {
      if (isBulkChecked || isBulkIndeterminate) {
        dispatch(selectAllBulkAction(certKey, true))
          .then(() => {
            dispatch(setCheckForBulkAction(false));
            dispatch(setIndeterminateForBulkAction(false));
            dispatch(setAlteastOneIsSelected(false));
          })
          .catch((error) => {
            dispatch(setBulkActionAlert(true, error.message));
          })
          .finally(() => {
            // Call group by mechanisms here if
            dispatch(setGroupByOptions(updatedGroupByOptions));
            dispatch(setCertTriggerFromSearchOrFilter(true));
            dispatch(
              getCertDetailsData(
                certKey,
                columnNames,
                ITEMS_API_OFFSET,
                0,
                searchText
              )
            );
          });

        return;
      }

      dispatch(setGroupByOptions(updatedGroupByOptions));
      dispatch(setCertTriggerFromSearchOrFilter(true));
      resetMergeList();
      dispatch(
        getCertDetailsData(
          certKey,
          columnNames,
          ITEMS_API_OFFSET,
          0,
          searchText
        )
      );
    }
  };

  return (
    <Menu
      options={groupByOptions.map((item) => {
        const updatedItem = { ...item }; // Create a new object

        updatedItem.label = l(item.label);

        return updatedItem;
      })}
      onChange={handleGroupByChange}
      placement={isTabletXSmallWidth ? 'bottom-start' : 'bottom-end'}
      fallbackPlacements={['bottom', 'bottom-start']}
      trigger={
        <ButtonSelect
          kind={isButtonKindIcon ? 'rectangle' : 'icon'}
          label={label}
          size='medium'
          BadgeComp={
            isBadgeGroupBy ? (
              <Badge kind='dot' color='primary' size='small' />
            ) : null
          }
          prefixIcon={<Icon kind='Dashboard' />}
          isDisabled={activeTabCount === 0 || isLoading}
          isVisualCueEnabled={false}
          className='CertsGroupByButton'
        />
      }
      className='CertsGroupByMenu'
    />
  );
}

GroupByButton.propTypes = {
  isBadgeGroupBy: PropTypes.bool.isRequired,
  groupByOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isButtonKindIcon: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  resetMergeList: PropTypes.func.isRequired,
};

export default GroupByButton;
